/**
 * ChangeSetReviewPage.tsx — one change set, opened by anyone who may review it.
 *
 * The authoring screens hold the change set their own edits opened, and nobody
 * else's. Review is the other half of §8: an engineer submits, and a DIFFERENT
 * person approves and publishes. Without a screen that opens a change set by its
 * identity, that second person had nowhere in this workbench to do it.
 *
 * The page adopts the change set the Engine returns and hands it to the same
 * controller and panel the authoring screens use, so the state machine, the
 * separation-of-duties refusal and the conflict report are the Engine's and are
 * rendered exactly as they are everywhere else. Nothing about the change set is
 * remembered between visits; it is read on every open.
 */

import { useEffect } from "react";
import { useClient } from "../../api/ClientContext";
import { routeHref } from "../../app/routing";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";
import { ChangeSetPanel, ConflictNotice } from "./ChangeSetPanel";
import { useChangeSet } from "./useChangeSet";

export function ChangeSetReviewPage({ projectId, changeSetId }: { projectId: string; changeSetId: string }): JSX.Element {
  const client = useClient();
  const controller = useChangeSet(projectId);
  const loaded = useAsync(() => client.getChangeSet(projectId, changeSetId), [client, projectId, changeSetId]);
  const { adopt } = controller;

  useEffect(() => {
    if (loaded.data !== undefined) adopt(loaded.data);
  }, [adopt, loaded.data]);

  return (
    <div className="page" data-stage="change-set-review" data-change-set-id={changeSetId}>
      <header className="page-head">
        <h1>Change set review</h1>
        <p className="muted">
          Read from the Engine on this open. Who may approve and who may publish is the Engine&apos;s decision; a
          refusal is shown as the Engine states it.
        </p>
        <a className="ghost" href={routeHref({ name: "project", projectId })}>Back to the project</a>
      </header>
      <ErrorBanner error={loaded.error} onDismiss={loaded.reload} />
      <ErrorBanner error={controller.error} onDismiss={controller.clearError} />
      {controller.conflict && (
        <ConflictNotice conflict={controller.conflict} onReload={loaded.reload} onDismiss={controller.clearError} />
      )}
      {loaded.loading && controller.changeSet === undefined ? <Empty>Retrieving the change set from the Engine…</Empty> : null}
      {controller.changeSet !== undefined && <ChangeSetPanel controller={controller} />}
    </div>
  );
}
